import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { AcademyPromise } from "../components/AcademyPromise";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Lock, Clock, BookOpen, Award, ShieldCheck,
} from "lucide-react";

const MODULE_BADGE = {
  "gs-welcome": "golden-five-graduate",
  "gs-weather": "weather-ready-pilot",
};

export default function AcademyView() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [academy, setAcademy] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get(`/academies/${slug}`), api.get("/progress"), api.get("/badges")])
      .then(([a, p, b]) => {
        setAcademy(a.data);
        setCompleted(p.data.completed_lessons || []);
        setEarnedBadges(p.data.earned_badges || []);
        setBadgeDefs(b.data || []);
      })
      .catch(() => nav("/dashboard"))
      .finally(() => setLoading(false));
  }, [slug, nav]);

  if (loading || !academy)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const Icon = getIcon(academy.icon);
  const allIds = academy.modules.flatMap((m) => m.lesson_ids || []);
  const doneCount = allIds.filter((id) => completed.includes(id)).length;
  const pct = allIds.length ? Math.round((doneCount / allIds.length) * 100) : 0;

  const moduleProgress = (m) => {
    const ids = m.lesson_ids || [];
    const done = ids.filter((id) => completed.includes(id)).length;
    return { done, total: ids.length, pct: ids.length ? Math.round((done / ids.length) * 100) : 0 };
  };

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-academies">
          <ArrowLeft className="h-4 w-4" /> All Academies
        </Link>

        {academy.slug === "ground-school" && <AcademyPromise />}

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="relative rounded-sm border border-border/70 bg-card overflow-hidden mb-10">
          <div className="absolute inset-0">
            <img src={academy.thumbnail} alt={academy.title} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-card via-card/90 to-card/60" />
          </div>
          <div className="relative p-7 md:p-9">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl" aria-hidden>{academy.emoji}</span>
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Academy</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold flex items-center gap-3">
              <Icon className="h-8 w-8 text-primary shrink-0" /> {academy.title}
            </h1>
            <p className="text-slate-300 mt-3 max-w-2xl leading-relaxed">{academy.description}</p>
            <div className="flex items-center gap-6 mt-5 text-sm font-mono text-muted-foreground">
              <span className="flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-primary" /> {academy.module_count} modules</span>
              <span className="flex items-center gap-1.5"><Award className="h-4 w-4 text-primary" /> {academy.lesson_count} lessons</span>
            </div>
            <div className="flex items-center gap-4 mt-5 max-w-md">
              <Progress value={pct} className="h-2 bg-secondary" />
              <span className="font-mono text-sm text-primary shrink-0">{pct}%</span>
            </div>
          </div>
        </motion.div>

        <h2 className="font-display text-2xl font-bold mb-5">Curriculum</h2>
        <div className="space-y-3" data-testid="academy-modules">
          {academy.modules.map((m, i) => {
            const MIcon = getIcon(m.icon);
            const mp = moduleProgress(m);
            const lifetimeGS = academy.slug === "ground-school" && !!user?.ground_school_lifetime_access;
            const locked = m.premium && !user?.has_paid && !lifetimeGS;
            const soon = !!m.coming_soon;
            const complete = mp.total > 0 && mp.pct === 100;
            const to = soon ? null : (locked ? "/checkout" : `/module/${m.id}`);
            const badgeId = MODULE_BADGE[m.id];
            const badgeDef = badgeId ? badgeDefs.find((d) => d.id === badgeId) : null;
            const badgeEarned = badgeId ? earnedBadges.includes(badgeId) : false;
            const cardInner = (
              <>
                  <div className="h-11 w-11 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                    <MIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{m.level}</span>
                      {soon && (
                        <span data-testid={`module-coming-soon-${m.id}`} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                          <Lock className="h-3 w-3" /> Coming soon
                        </span>
                      )}
                      {!soon && locked && (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1">
                          <Lock className="h-3 w-3" /> Premium
                        </span>
                      )}
                      {complete && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <h3 className="font-display font-bold text-base md:text-lg truncate">{m.title}</h3>
                    <p className="text-sm text-slate-400 line-clamp-1">{m.summary}</p>
                    {badgeDef && (
                      <span data-testid={`module-badge-teaser-${m.id}`}
                        className={`inline-flex items-center gap-1.5 mt-2 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${
                          badgeEarned
                            ? "bg-primary/10 text-primary border-primary/40"
                            : "bg-accent/10 text-accent border-accent/40"}`}>
                        <Award className="h-3 w-3" />
                        {badgeEarned ? `${badgeDef.name} — Earned` : `Earn: ${badgeDef.name}`}
                      </span>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <Progress value={mp.pct} className="h-1.5 bg-secondary max-w-[180px]" />
                      <span className="text-xs font-mono text-muted-foreground">{mp.done}/{mp.total}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              </>
            );
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }} data-testid={`academy-module-${m.id}`}>
                {soon ? (
                  <div aria-disabled="true" data-testid={`academy-module-link-${m.id}`}
                    className="flex items-center gap-4 p-5 rounded-sm border border-border/70 bg-card opacity-60 cursor-not-allowed select-none">
                    {cardInner}
                  </div>
                ) : (
                  <Link to={to} data-testid={`academy-module-link-${m.id}`}
                    className="flex items-center gap-4 p-5 rounded-sm border border-border/70 bg-card hover:border-primary/50 transition-colors group">
                    {cardInner}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>

        {academy.slug === "ground-school" && (
          <div className="mt-8 p-5 rounded-sm border border-accent/30 bg-accent/5 flex items-start gap-3" data-testid="capstone-note">
            <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <p className="text-sm text-slate-300">
              Complete every module to reach the <span className="text-accent font-medium">Ground School Review</span> capstone and earn your Certificate of Completion.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
